import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './SurveyPage.css'

const API = process.env.REACT_APP_API_URL || ""

const FEATURES = [
    {
        tag: 'GRID PLANNING',
        title: 'Plan your survey in seconds.',
        desc: 'Draw the area on the map. The app calculates the grid flight path and overlap automatically.',
        key: 'survey_plan',
        stat: { val: '90%', label: 'Image Overlap' },
    },
    {
        tag: 'GROUND CONTROL',
        title: 'Place GCPs. Get precision.',
        desc: 'Mark and manage ground control points on the map. Supports unlimited GCPs per mission.',
        key: 'survey_gcp',
        stat: { val: '2.1 cm', label: 'GSD Accuracy' },
    },
    {
        tag: 'LIVE MONITORING',
        title: 'Track the survey live.',
        desc: 'See coverage progress, camera triggers and drone position on one screen in real time.',
        key: 'survey_monitor',
        stat: { val: '1 Hz', label: 'Update Rate' },
    },
    {
        tag: 'DATA EXPORT',
        title: 'Export and review easily.',
        desc: 'Download flight logs and waypoints as KML or GeoJSON. Compatible with QGIS, ArcGIS and Pix4D.',
        key: 'survey_export',
        stat: { val: '100%', label: 'Log Accuracy' },
    },
]

function useCountUp(target, duration, active) {
    const [val, setVal] = useState(0)
    useEffect(() => {
        if (!active) return
        let start = 0
        const step = target / (duration / 16)
        const t = setInterval(() => {
            start += step
            if (start >= target) { setVal(target); clearInterval(t) }
            else setVal(Math.floor(start))
        }, 16)
        return () => clearInterval(t)
    }, [active, target, duration])
    return val
}

function HeroStats() {
    const ref = useRef(null)
    const [on, setOn] = useState(false)
    useEffect(() => {
        const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setOn(true) }, { threshold: 0.5 })
        if (ref.current) o.observe(ref.current)
        return () => o.disconnect()
    }, [])
    const a = useCountUp(90, 1200, on)
    const b = useCountUp(21, 1400, on)
    const c = useCountUp(15, 1000, on)
    return (
        <div ref={ref} className="svh-stats">
            <div className="svh-stat"><span className="svh-stat-val sv-mono">{a}%</span><span className="svh-stat-lbl">Image Overlap</span></div>
            <div className="svh-stat-div" />
            <div className="svh-stat"><span className="svh-stat-val sv-mono">2.{b} cm</span><span className="svh-stat-lbl">GSD Accuracy</span></div>
            <div className="svh-stat-div" />
            <div className="svh-stat"><span className="svh-stat-val sv-mono">{c} KM²</span><span className="svh-stat-lbl">Max Area / Flight</span></div>
        </div>
    )
}

function FeatureCard({ f, index, mediaUrl, isVisible, observerRef }) {
    const cardRef = useRef(null)

    useEffect(() => {
        if (observerRef) observerRef(cardRef.current, index)
    }, [index, observerRef])

    const handleMouseMove = (e) => {
        const card = cardRef.current
        if (!card) return
        const rect = card.getBoundingClientRect()
        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        const shimmer = card.querySelector('.sv-img-shimmer')
        if (shimmer) shimmer.style.background = `radial-gradient(circle at ${x}px ${y - rect.height * 0.55}px, rgba(255,255,255,0.12), transparent 60%)`
    }

    const handleMouseLeave = () => {
        const card = cardRef.current
        if (!card) return
        const shimmer = card.querySelector('.sv-img-shimmer')
        if (shimmer) shimmer.style.background = ''
    }

    return (
        <div
            ref={cardRef}
            className={`sv-fc-card ${isVisible ? 'sv-fc-card-in' : ''}`}
            style={{ transitionDelay: `${index * 0.1}s` }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            <div className="sv-fc-img-wrap">
                {mediaUrl
                    ? <img src={mediaUrl} alt={f.tag} className="sv-fc-img" />
                    : (
                        <div className="sv-fc-img-empty">
                            <span className="sv-mono" style={{ color: '#4a6fa5', fontSize: 11 }}>{f.key}</span>
                            <span style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>Upload via Media Manager</span>
                        </div>
                    )
                }
                <div className="sv-img-shimmer" />
                <div className="sv-fc-stat-chip sv-mono">
                    <span className="sv-fc-stat-val">{f.stat.val}</span>
                    <span className="sv-fc-stat-label">{f.stat.label}</span>
                </div>
            </div>
            <div className="sv-fc-body">
                <span className="sv-fc-tag sv-mono">{f.tag}</span>
                <h3 className="sv-fc-card-title">{f.title}</h3>
                <p className="sv-fc-desc">{f.desc}</p>
                <span className="sv-fc-arrow sv-mono">Explore →</span>
            </div>
        </div>
    )
}

export default function SurveyPage() {
    const [media, setMedia] = useState({})
    const [textIn, setTextIn] = useState(false)
    const [visible, setVisible] = useState([])
    const observerMap = useRef({})

    useEffect(() => {
        document.title = "Drone Commander GCS - Surveying & Mapping | Aviatricks";
        let metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
            metaDesc.setAttribute("content", "Professional survey-grade photogrammetric mapping and terrain analysis using Drone Commander GCS. Orthomosaics, DEMs, and point clouds.");
        }

        fetch(`${API}/api/media/survey`)
            .then(r => r.json())
            .then(d => { setMedia(d) })
            .catch(() => { })
        setTimeout(() => setTextIn(true), 400)
    }, [])

    const attachObserver = (el, i) => {
        if (!el || observerMap.current[i]) return
        const o = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setVisible(v => [...new Set([...v, i])]) },
            { threshold: 0.12 }
        )
        o.observe(el)
        observerMap.current[i] = o
    }

    return (
        <div className="sv-wrap">

            {/* ── HERO ── */}
            <section className="svh-hero">
                {media.survey_hero_bg && (
                    <img src={media.survey_hero_bg} alt="" className="svh-bg" />
                )}
                <div className="svh-overlay" />
                <div className="svh-scanlines" />
                <div className="svh-frame svh-tl" /><div className="svh-frame svh-tr" />
                <div className="svh-frame svh-bl" /><div className="svh-frame svh-br" />

                <div className="svh-topbar sv-mono">
                    <div className="svh-topbar-l"><span className="svh-live" />DRONE COMMANDER GCS — SURVEY</div>
                    <div className="svh-topbar-c">LAT/LON: 13.0827 N, 80.2707 E &nbsp;|&nbsp; ALT: 80M &nbsp;|&nbsp; GSD: 2.1 CM/PX</div>
                    <div className="svh-topbar-r">CAM: ACTIVE &nbsp;|&nbsp; IMAGES: 124</div>
                </div>
                <div className="svh-botbar sv-mono">
                    <span>GRID MISSION &nbsp;·&nbsp; OVERLAP: 90%</span>
                    <span>GPS LOCKED &nbsp;·&nbsp; RTK ON &nbsp;·&nbsp; 4K</span>
                </div>

                <div className={`svh-content ${textIn ? 'svh-in' : ''}`}>
                    <div className="svh-eyebrow sv-mono">
                        <span className="svh-badge">SURVEY</span>
                        Aviatricks GCS
                    </div>
                    <h1 className="svh-h1">
                        <span className="svh-line svh-l1">Map anything.</span>
                        <span className="svh-line svh-l2">Miss nothing.</span>
                    </h1>
                    <p className="svh-p">
                        A simple app to plan, fly and capture precise survey data.<br />
                        Built for surveyors and mapping professionals.
                    </p>
                    <div className="svh-btns">
                        <a href="#features" className="svh-btn-primary">Explore Features ↓</a>
                        <Link to="/contact/support" className="svh-btn-ghost">Get in Touch</Link>
                    </div>
                    <HeroStats />
                </div>

                <div className="svh-scroll sv-mono">scroll to explore</div>
            </section>

            {/* ── FEATURE CARDS ── */}
            <section className="sv-fc-section" id="features">
                {/* Grid lines — same as eco-bg-grid */}
                <div className="sv-bg-grid" />

                <div className="sv-fc-container">
                    <div className="sv-fc-header">
                        <span className="sv-fc-label sv-mono">CAPABILITIES</span>
                        <h2 className="sv-fc-title">Simple to use.<br />Precise in the field.</h2>
                        <p className="sv-fc-sub">Plan grid missions, place GCPs, monitor live, and export data — all from one app.</p>
                    </div>
                    <div className="sv-fc-grid">
                        {FEATURES.map((f, i) => (
                            <FeatureCard
                                key={f.key}
                                f={f}
                                index={i}
                                mediaUrl={media[f.key]}
                                isVisible={visible.includes(i)}
                                observerRef={attachObserver}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="sv-cta">
                {/* Grid lines — same as eco-bg-grid */}
                <div className="sv-bg-grid" />

                <div className="sv-cta-inner">
                    <span className="sv-mono sv-cta-label">Drone Commander GCS — Survey</span>
                    <h2 className="sv-cta-title">Ready to map<br />smarter?</h2>
                    <p className="sv-cta-tagline">Precision mapping, elevated.</p>
                    <p className="sv-cta-p">Get in touch and we'll set you up with Drone Commander GCS for your survey work.</p>
                    <Link to="/contact/support" className="svh-btn-primary" style={{ fontSize: 15, padding: '15px 36px' }}>Request a Demo</Link>
                </div>
            </section>

        </div>
    )
}